import { QueryClient, useQuery } from "@tanstack/react-query";
import { SystemAPI } from "./system.api";
import {
  GetSystemInfoOptions,
  GetSystemInfoResponse,
  UseGetSystemInfo,
} from "./system.types";

const SystemQueryKeys = {
  info: ["system", "info"] as const,
};

export const useGetSystemInfo: UseGetSystemInfo = (options) => {
  return useQuery({
    queryKey: SystemQueryKeys.info,
    queryFn: () => SystemAPI.getSystemInfo(),
    ...options,
  });
};

// Forces a fresh read of /system/info and updates the shared cache with it.
// httpGet resolves `{ success: false, error }` instead of throwing, and
// fetchQuery would store that as data for every observer on the key (the
// deployment alerts, the Settings storage meter), so the queryFn rejects on
// any response that is not a real system-info payload. Callers catch.
export const fetchSystemInfo = (
  queryClient: QueryClient,
  options?: GetSystemInfoOptions
) =>
  queryClient.fetchQuery<GetSystemInfoResponse>({
    queryKey: SystemQueryKeys.info,
    queryFn: async () => {
      const info = await SystemAPI.getSystemInfo(options);
      if (!Number.isFinite(info?.files?.total_size)) {
        throw new Error("System info unavailable");
      }
      return info;
    },
    staleTime: 0,
    retry: false,
  });

export const SystemQuery = {
  queryKeys: SystemQueryKeys,
  useGetSystemInfo,
  fetchSystemInfo,
};
